import { useMutation } from "@tanstack/react-query";
import type { LoginFormData } from "@/features/auth/schemas/forms/loginForm.schema";
import * as authService from "@/features/auth/services/auth.service";
import { authStorageService } from "@/features/auth/services/storage.service";
import type {
  AuthCheck2FAPayload,
  AuthCheck2FAResponse, AuthErrorResponse, AuthRegisterPhonePayload, AuthRegisterPhoneResponse, AuthResponse, RefreshTokenResponse,
  AuthResetPasswordPayload, AuthResetPasswordResponse,
  AuthSetPasswordPayload, AuthSetPasswordResponse,
  AuthLoginResponse,
  ImpersonatePayload,
} from "@/features/auth/types/auth.type";
import { clearCmsPreviewToken } from "@/features/shop/cms/preview/cmsPreviewToken";
import { queryClient } from "@/shared/lib/query-client";

type UseAuthMutationsProps = {
  onLoginSuccess: (data: AuthResponse) => void
  onCheck2FASuccess: (data: AuthCheck2FAResponse) => void
  onRefreshSessionSuccess: (data: RefreshTokenResponse) => void
  onRefreshSessionFailed: () => void
  onError: (error: AuthErrorResponse) => void
  onResetPasswordSuccess?: (data: AuthResetPasswordResponse | AuthErrorResponse) => void
  onSetPasswordSuccess?: (data: AuthSetPasswordResponse | AuthErrorResponse) => void
};

export const useAuthMutations = ({
  onLoginSuccess,
  onError,
  onCheck2FASuccess,
  onRefreshSessionSuccess,
  onRefreshSessionFailed,
  onResetPasswordSuccess,
  onSetPasswordSuccess,
}: UseAuthMutationsProps) => {
  const loginMutation = useMutation({
    mutationFn: (payload: LoginFormData): Promise<AuthResponse> =>
      authService.loginUser({
        username: payload.email,
        password: payload.password,
        hcaptcha: payload.hcaptcha ?? null,
      }),
    onSuccess: onLoginSuccess,
    onError,
  });

  const check2FAMutation = useMutation({
    mutationFn: (payload: AuthCheck2FAPayload): Promise<AuthCheck2FAResponse> =>
      authService.check2FA(payload),
    onSuccess: onCheck2FASuccess,
    onError,
  });

  const registerPhoneMutation = useMutation({
    mutationFn: (payload: AuthRegisterPhonePayload): Promise<AuthRegisterPhoneResponse> =>
      authService.registerPhone(payload),
    onSuccess: onLoginSuccess,
    onError,
  });

  const refreshSessionMutation = useMutation<RefreshTokenResponse, AuthErrorResponse, void>({
    mutationFn: (): Promise<RefreshTokenResponse> => authService.refreshToken(),
    onSuccess: onRefreshSessionSuccess,
    onError: onRefreshSessionFailed,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (payload: AuthResetPasswordPayload): Promise<AuthResetPasswordResponse | AuthErrorResponse> =>
      authService.resetPassword(payload),
    onSuccess: onResetPasswordSuccess,
    onError,
  });

  const setPasswordMutation = useMutation({
    mutationFn: (payload: AuthSetPasswordPayload): Promise<AuthSetPasswordResponse | AuthErrorResponse> =>
      authService.setPassword(payload),
    onSuccess: onSetPasswordSuccess,
    onError,
  });

  const logoutMutation = useMutation<void>({
    mutationFn: async () => {
      const capturedToken = authStorageService.getAuthToken();
      authStorageService.deleteAuthToken();
      await queryClient.cancelQueries();

      // Clear auth-related cached data to prevent stale data on re-login
      queryClient.removeQueries({ queryKey: ["user"] });
      queryClient.removeQueries({ queryKey: ["organization"] });
      queryClient.removeQueries({ queryKey: ["plan"] });
      queryClient.removeQueries({ queryKey: ["cmsPages"] });
      // Un aperçu de brouillon CMS appartient à la session qui l'a ouvert.
      clearCmsPreviewToken();

      await authService.logoutUser(capturedToken ?? undefined);
    },
    retry: false,
  });

  const impersonateMutation = useMutation({
    mutationFn: (payload: ImpersonatePayload): Promise<AuthLoginResponse> => authService.impersonate(payload),
    onSuccess: onLoginSuccess,
    onError,
  });

  const exchangeGrantMutation = useMutation({
    mutationFn: (loginToken: string): Promise<AuthLoginResponse> => authService.exchangeGrantLogin(loginToken),
    onSuccess: onLoginSuccess,
    onError,
  });

  return {
    loginMutation,
    check2FAMutation,
    registerPhoneMutation,
    refreshSessionMutation,
    resetPasswordMutation,
    setPasswordMutation,
    logoutMutation,
    impersonateMutation,
    exchangeGrantMutation,
  };
};
